/**
 * Practice weeks in the Game Library (client 2026-10-06): a game can be filed
 * under a week ("Week 3", "Bye week"…). There is no weeks list on the server —
 * a team's weeks are the names its games carry — so the choices offered are
 * the week names already on this app's games, plus whatever the coach types.
 */

import type { LibraryGame } from "./adapter.ts";

/** The API's column length (varchar(60)). */
export const WEEK_NAME_MAX_LENGTH = 60;

/**
 * A typed week name as it is stored — the API does the same: trimmed, inner
 * runs of spaces collapsed, cut to the column by character (as varchar
 * counts, so an emoji is never split). Null when nothing is left.
 */
export const normalizeWeekName = (raw: string | null | undefined): string | null => {
  const name = Array.from(String(raw ?? "").trim().replace(/\s+/g, " "))
    .slice(0, WEEK_NAME_MAX_LENGTH)
    .join("")
    .trimEnd();
  return name || null;
};

/** "Week 2" before "Week 10", case aside. */
const compareWeeks = (a: string, b: string) =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });

/**
 * The weeks to offer: every week name on these games, once each (the first
 * spelling seen wins over "week 3" vs "Week 3"), in week order.
 */
export const libraryWeekNames = (games: readonly LibraryGame[] | null | undefined): string[] => {
  const byKey = new Map<string, string>();
  for (const game of games ?? []) {
    const name = normalizeWeekName(game.week_name);
    if (name && !byKey.has(name.toLowerCase())) byKey.set(name.toLowerCase(), name);
  }
  return [...byKey.values()].sort(compareWeeks);
};

/** The weeks to offer with `week` among them (one just typed, not yet on a game). */
export const withWeek = (weeks: readonly string[], week: string | null): readonly string[] =>
  !week || weeks.some((w) => sameWeek(w, week)) ? weeks : [...weeks, week].sort(compareWeeks);

/** The same week, whatever the capitals ("week 3" is the team's "Week 3"). */
export const sameWeek = (a: string | null, b: string | null): boolean =>
  a === b || (a != null && b != null && a.toLowerCase() === b.toLowerCase());

/** The picker's own "no week" choice — not a name a week can take. */
const NO_WEEK = "no week";

/**
 * A newly typed week, matched to an existing one regardless of case — typing
 * "week 3" files the game under the team's "Week 3", not a second week.
 * Null for nothing, or for "No week" (that is the list's first choice).
 */
export const resolveTypedWeek = (raw: string, weeks: readonly string[]): string | null => {
  const name = normalizeWeekName(raw);
  if (!name || name.toLowerCase() === NO_WEEK) return null;
  return weeks.find((week) => week.toLowerCase() === name.toLowerCase()) ?? name;
};
